import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { billing } from '@repletoryreach/sdk-api';

/**
 * The cross-tenant journal, read a keyset page at a time.
 *
 * `GET /v1/admin/ledger` used to answer one page and stop, so a wide date range
 * over a busy platform showed a sample of the newest N entries and there was no
 * way to reach the rest. It now orders by `occurred_at DESC, id DESC` and
 * answers `meta.next_cursor`, so the page can go on asking until the cursor is
 * gone. `meta.total` counts the whole window, which is what tells the operator
 * how much is still behind the Load more button.
 *
 * The rest of the page filters, sorts and exports over whatever is loaded, so
 * loading more genuinely widens the search rather than only lengthening the
 * table.
 */
export function useLedgerPages(opts: { org_id?: string; limit: number }) {
  const query = useInfiniteQuery({
    queryKey: ['admin', 'ledger', 'pages', opts.org_id ?? null, opts.limit],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      billing.listAdminLedger({ org_id: opts.org_id, limit: opts.limit, cursor: pageParam }),
    // Absent on the last page, which is what stops the walk.
    getNextPageParam: (last) => last.meta.next_cursor ?? undefined
  });

  const pages = query.data?.pages;

  const rows = useMemo(() => pages?.flatMap((p) => p.data) ?? [], [pages]);

  return {
    rows,
    /** Every entry matching the server-side filters, not just the loaded ones. */
    total: pages?.[0]?.meta.total ?? rows.length,
    /** True while the first page is in flight; a later page uses `isFetchingNextPage`. */
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    refetch: query.refetch
  };
}
